import { List } from 'immutable';

import { accountsTypes } from '../accounts';
import type { Account, AccountSubType, AccountTypeNode } from '../accounts/accountsTypes';
import type { AccountData, DiscoveredAccount } from './accountDiscoveryTypes';

type AccountsById = {
  get(id: string): Account | undefined;
};

export function replaceRawASCII(string: string | null | undefined): string | undefined {
  return string?.replace(/&#(.*?);/g, (code) => {
    const numString = code.replace(/&#|x|;/g, '');
    const num = Number.parseInt(numString, code[2] === 'x' ? 16 : 10);
    return String.fromCharCode(num);
  });
}

export type NewAccountFields = {
  name: string | null | undefined;
  type: ReturnType<typeof accountsTypes.getAccountTypeNodeByQcsType> | undefined;
  usageId: string;
  linkToId: string;
};

// Discovered and linked accounts always resolve a type; only the Mint variant leaves it unset.
export function getNewAccountFields(
  account: AccountData,
  linkToAccountId: string | null | undefined,
  accountsById: AccountsById,
): NewAccountFields & { type: AccountTypeNode } {
  if (linkToAccountId && linkToAccountId !== '0') {
    const linkToAccount = accountsById.get(linkToAccountId)!;
    return {
      name: linkToAccount.name,
      type: accountsTypes.getAccountTypeNodeByQcsType(linkToAccount.type, linkToAccount.subType),
      usageId: linkToAccount.businessId || (linkToAccount.usageType === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL'),
      linkToId: linkToAccountId,
    };
  }
  const discoveredAccount = account.discoveredAccount!;
  return {
    name: discoveredAccount.name,
    type: accountsTypes.getAccountTypeNodeByQcsType(
      discoveredAccount.type!,
      discoveredAccount.subType as AccountSubType | null | undefined,
    ),
    usageId: discoveredAccount.businessId ?? 'PERSONAL',
    linkToId: '0',
  };
}

//we use a diff account structure and flow for mint import
export function getNewAccountFieldsMint(
  account: { accountName?: string | null },
  linkToAccountId: string | null | undefined,
  accountsById: AccountsById,
): NewAccountFields {
  if (linkToAccountId && linkToAccountId !== '0') {
    const linkToAccount = accountsById.get(linkToAccountId)!;
    return {
      name: linkToAccount.name,
      type: accountsTypes.getAccountTypeNodeByQcsType(linkToAccount.type, linkToAccount.subType),
      usageId: linkToAccount.businessId || (linkToAccount.usageType === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL'),
      linkToId: linkToAccountId,
    };
  }
  return {
    name: account.accountName,
    type: undefined,
    usageId: 'PERSONAL',
    linkToId: '0',
  };
}

// QCS can leave a discovered account's type out; UNKNOWN makes the user pick one.
export function transformDiscoveredAccountsToAccounts(discoveredAccounts: List<DiscoveredAccount>): Account[] {
  return discoveredAccounts
    .map((discoveredAccount) =>
      accountsTypes.mkAccount(discoveredAccount.type ?? 'UNKNOWN', {
        aggregators: List([
          accountsTypes.mkAccountAggregator({
            cpId: discoveredAccount.cpId,
            cpLoginId: discoveredAccount.cpLoginId,
            channel: discoveredAccount.channel,
            subType: discoveredAccount.subType,
            accountName: discoveredAccount.name,
            accountNumberMasked: discoveredAccount.maskedNumber,
          }),
        ]),
      }),
    )
    .toArray();
}

const accountsLabel = (count: number): string => `${count} account${count === 1 ? '' : 's'}`;

export const getNoNewAccountsMessage = (alreadyAddedCount: number, newAccountsCount: number): string => {
  const discoveredCount = alreadyAddedCount + newAccountsCount;
  if (discoveredCount === 0) {
    return "We didn't find any accounts at this bank.";
  }
  const found = `We found ${accountsLabel(discoveredCount)} at this bank.`;
  if (newAccountsCount === 0) {
    return alreadyAddedCount === 1
      ? `${found} It's already added to Simplifi.`
      : `${found} All ${alreadyAddedCount} are already added to Simplifi.`;
  }
  return `${found} There are no new accounts you can add to Simplifi.`;
};
